/**
 * ManualWhatsAppCredentialsDialog
 *
 * Permanent fallback to Embedded Signup. Used when:
 *   - Embedded Signup is unavailable / under review
 *   - Meta access token has expired and needs replacement
 *   - Onboarding fallback for customers without Embedded Signup support
 *
 * Security:
 *   - Access Token is a password-style input
 *   - Existing token is NEVER displayed (UI shows status, not the value)
 *   - Form state is wiped on successful close (no persistence)
 *   - Token is never echoed back from the API
 *
 * One dialog, two trigger points:
 *   1. Top "Manual Setup" button (no preselected channel — user picks)
 *   2. Per-row "Update Credentials" / "Fix Connection" button (channel preselected)
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Loader2, ShieldAlert, Eye, EyeOff, KeyRound } from 'lucide-react';
import { API, useAuth } from '../App';

const EMPTY_FORM = {
  access_token: '',
  phone_number_id: '',
  waba_id: '',
  business_account_id: '',
};

const ManualWhatsAppCredentialsDialog = ({
  open,
  onOpenChange,
  channels = [],
  preselectedChannelId = null,
  onSaved = () => {},
  // ── ADDITIVE PROPS (Feb 2026) — when omitted the dialog renders
  // exactly as before. The Update path is byte-for-byte identical.
  allowCreate = false,
  defaultMode = 'update',
  limits = null,
  onChannelCreated = null,
}) => {
  const { token } = useAuth();
  const authHeaders = { Authorization: `Bearer ${token}` };

  const [channelId, setChannelId] = useState(preselectedChannelId || '');
  const [form, setForm] = useState(EMPTY_FORM);
  const [showToken, setShowToken] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Mode + Create-only fields. Render-gated on `allowCreate`.
  const _hasExisting = (channels || []).length > 0;
  const _initialMode = !allowCreate
    ? 'update'
    : (_hasExisting ? defaultMode : 'create');
  const [mode, setMode] = useState(_initialMode);
  const [displayName, setDisplayName] = useState('');

  // Keep local channelId in sync with the preselect prop, but only when
  // the dialog actually opens — never overwrite while the user is typing.
  useEffect(() => {
    if (open) {
      setChannelId(preselectedChannelId || (channels[0]?.id ?? ''));
      setForm(EMPTY_FORM);
      setShowToken(false);
      setDisplayName('');
      const _initial = !allowCreate
        ? 'update'
        : (_hasExisting ? defaultMode : 'create');
      setMode(_initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preselectedChannelId, channels, allowCreate, defaultMode]);

  const reset = () => {
    setForm(EMPTY_FORM);
    setShowToken(false);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    // ── CREATE MODE branch — additive, gated on `allowCreate`. The
    // existing UPDATE path below is byte-for-byte unchanged.
    if (allowCreate && mode === 'create') {
      if (limits && limits.allowed === false) {
        toast.error(
          `Channels used: ${limits.current_count}/${limits.max_allowed}. Upgrade your plan to add more.`
        );
        return;
      }
      const _name = (displayName || '').trim();
      if (!_name) {
        toast.error('Channel name is required');
        return;
      }
      if (!form.access_token || !form.phone_number_id || !form.waba_id) {
        toast.error('Access Token, Phone Number ID, and WABA ID are required');
        return;
      }
      setSubmitting(true);
      try {
        const res = await axios.post(
          `${API}/channels/whatsapp/manual-create`,
          {
            display_name: _name,
            access_token: form.access_token,
            phone_number_id: form.phone_number_id.trim(),
            waba_id: form.waba_id.trim(),
            business_account_id: form.business_account_id.trim() || undefined,
          },
          { headers: authHeaders, validateStatus: () => true },
        );
        if (res.status >= 400 || res.data?.success === false) {
          const msg = res.data?.error || res.data?.detail?.error
            || res.data?.detail || 'Channel create failed';
          toast.error(typeof msg === 'string' ? msg : 'Channel create failed');
          setForm((f) => ({ ...f, access_token: '' }));
          setSubmitting(false);
          return;
        }
        toast.success('WhatsApp channel created');
        if (onChannelCreated) onChannelCreated(res.data?.channel);
        onSaved(res.data);
        reset();
        setDisplayName('');
        onOpenChange(false);
      } catch (err) {
        toast.error(err.response?.data?.detail || err.message || 'Create failed');
        setForm((f) => ({ ...f, access_token: '' }));
      } finally {
        setSubmitting(false);
      }
      return;
    }
    // ── UPDATE MODE — original code path, unchanged ─────────────────
    if (!channelId) {
      toast.error('Pick a channel first');
      return;
    }
    if (!form.access_token || !form.phone_number_id || !form.waba_id) {
      toast.error('Access Token, Phone Number ID, and WABA ID are required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/channels/${channelId}/whatsapp/credentials`,
        {
          access_token: form.access_token,
          phone_number_id: form.phone_number_id.trim(),
          waba_id: form.waba_id.trim(),
          business_account_id: form.business_account_id.trim() || undefined,
        },
        { headers: authHeaders, validateStatus: () => true },
      );
      if (res.status >= 400 || res.data?.success === false) {
        const msg = res.data?.error || res.data?.detail || 'Verification failed';
        toast.error(msg);
        // Wipe token from local state on failure too — never linger.
        setForm((f) => ({ ...f, access_token: '' }));
        setSubmitting(false);
        return;
      }
      toast.success('WhatsApp credentials updated');
      onSaved(res.data);
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Save failed');
      setForm((f) => ({ ...f, access_token: '' }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}
    >
      <DialogContent className="sm:max-w-lg" data-testid="manual-creds-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4" /> Manual WhatsApp Setup
          </DialogTitle>
          <DialogDescription>
            Use this if Embedded Signup is unavailable or you need to replace
            an expired Meta token. Both Embedded Signup and Manual Setup write
            to the same per-channel credentials.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {allowCreate && (
            <div
              className="flex gap-1 rounded-md bg-muted p-1 text-xs"
              data-testid="manual-creds-mode-tabs"
            >
              <button
                type="button"
                disabled={!_hasExisting}
                onClick={() => setMode('update')}
                data-testid="manual-creds-tab-update"
                className={
                  'flex-1 rounded px-3 py-1.5 transition-colors ' +
                  (mode === 'update'
                    ? 'bg-background shadow-sm font-medium'
                    : 'text-muted-foreground hover:text-foreground') +
                  (!_hasExisting ? ' opacity-50 cursor-not-allowed' : '')
                }
              >
                Update Existing
              </button>
              <button
                type="button"
                disabled={limits && limits.allowed === false}
                onClick={() => {
                  if (limits && limits.allowed === false) return;
                  setMode('create');
                }}
                data-testid="manual-creds-tab-create"
                className={
                  'flex-1 rounded px-3 py-1.5 transition-colors ' +
                  (mode === 'create'
                    ? 'bg-background shadow-sm font-medium'
                    : 'text-muted-foreground hover:text-foreground') +
                  (limits && limits.allowed === false
                    ? ' opacity-50 cursor-not-allowed'
                    : '')
                }
              >
                Create New
              </button>
            </div>
          )}

          {allowCreate && limits && (
            <div
              className="text-xs text-muted-foreground"
              data-testid="manual-creds-quota"
            >
              Channels used: <b>{limits.current_count ?? 0}</b> /{' '}
              <b>{limits.max_allowed ?? '—'}</b>
              {limits.allowed === false && (
                <span className="ml-1 text-amber-600">
                  · Your current plan allows {limits.max_allowed} WhatsApp
                  channel(s). Upgrade to add more.
                </span>
              )}
            </div>
          )}

          {allowCreate && mode === 'create' && (
            <div>
              <Label className="text-xs">
                Channel name <span className="text-red-500">*</span>
              </Label>
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. Sales WhatsApp"
                maxLength={80}
                data-testid="manual-creds-display-name"
              />
            </div>
          )}

          {(!allowCreate || mode === 'update') && !preselectedChannelId && (
            <div>
              <Label className="text-xs">Channel</Label>
              <Select value={channelId} onValueChange={setChannelId}>
                <SelectTrigger data-testid="manual-creds-channel-select">
                  <SelectValue placeholder="Select a channel..." />
                </SelectTrigger>
                <SelectContent>
                  {channels.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.display_name || c.name || c.id}
                      {c.display_phone_number ? ` — ${c.display_phone_number}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <Alert>
            <ShieldAlert className="h-4 w-4" />
            <AlertDescription className="text-xs">
              Credentials are stored encrypted, scoped to this channel only,
              and never echoed back. The current token (if any) is not shown
              here for security.
            </AlertDescription>
          </Alert>

          <div>
            <Label className="text-xs">Access Token <span className="text-red-500">*</span></Label>
            <div className="relative">
              <Input
                type={showToken ? 'text' : 'password'}
                autoComplete="off"
                spellCheck={false}
                value={form.access_token}
                onChange={(e) => setForm({ ...form, access_token: e.target.value })}
                placeholder="EAA…"
                data-testid="manual-creds-access-token"
                required
              />
              <button
                type="button"
                onClick={() => setShowToken((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showToken ? 'Hide token' : 'Show token'}
                data-testid="manual-creds-toggle-token"
              >
                {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Phone Number ID <span className="text-red-500">*</span></Label>
              <Input
                value={form.phone_number_id}
                onChange={(e) => setForm({ ...form, phone_number_id: e.target.value })}
                placeholder="e.g. 123456789012345"
                data-testid="manual-creds-phone-number-id"
                required
              />
            </div>
            <div>
              <Label className="text-xs">WABA ID <span className="text-red-500">*</span></Label>
              <Input
                value={form.waba_id}
                onChange={(e) => setForm({ ...form, waba_id: e.target.value })}
                placeholder="WhatsApp Business Account ID"
                data-testid="manual-creds-waba-id"
                required
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Business Account ID <span className="text-muted-foreground">(optional)</span></Label>
            <Input
              value={form.business_account_id}
              onChange={(e) => setForm({ ...form, business_account_id: e.target.value })}
              placeholder="Defaults to WABA ID if empty"
              data-testid="manual-creds-business-account-id"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={close}
              disabled={submitting}
              data-testid="manual-creds-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                submitting ||
                (allowCreate && mode === 'create' && limits && limits.allowed === false)
              }
              data-testid="manual-creds-save-btn"
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {allowCreate && mode === 'create' ? 'Create channel' : 'Verify & Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ManualWhatsAppCredentialsDialog;
